// src/App.js
import React, { useEffect, useRef } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Link,
  Navigate,
  useLocation,
  useNavigate,
} from "react-router-dom";
import * as tf from "@tensorflow/tfjs";

// ✅ Auth
import { AuthProvider } from "./auth/AuthProvider";
import Login from "./pages/Login.jsx";
import AuthCallback from "./pages/AuthCallback";

// ✅ Practice Timer (kept for premium user tracking — free tier has no timer)
import { PracticeTimerProvider } from "./hooks/PracticeTimerProvider";

// Pages
import LandingPage from "./pages/LandingPage";
import Intro from "./pages/Intro";
import ChapterOne from "./pages/ChapterOne";
import ChapterTwo from "./pages/ChapterTwo";
import ChapterFourPage from "./pages/ChapterFour.js";
import ChapterFivePage from "./pages/ChapterFive.js";
import ChapterSixPage from "./pages/ChapterSix.js";
import ChapterSevenPage from "./pages/ChapterSeven.js";
import ChapterEightPage from "./pages/ChapterEight.js";
import ChapterNinePage from "./pages/ChapterNine.js";
import ChapterTenPage from "./pages/ChapterTen.js";
import ConstellationPage from "./pages/ConstellationPage";
import SubscribePage from "./pages/SubscribePage";
import PaySuccess from "./pages/PaySuccess";

// ✅ NEW pages
import Help from "./pages/Help";
import SongBook from "./pages/SongBook";

// Finale pages
import DoronDefeated from "./pages/DoronDefeated";
import CompletionCertificate from "./pages/CompletionCertificate";

// Info pages
import About from "./pages/About";
import Contact from "./pages/Contact";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import Terms from "./pages/Terms";
import RefundPolicy from "./pages/RefundPolicy";

// ✅ NEW: Parents page
import Parents from "./pages/Parents";

// ✅ NEW: Store page
import Store from "./pages/Store";

// Guards
import RequireCh1to3Complete from "./components/RequireCh1to3Complete";
import RequirePremium from "./components/RequirePremium";

// NewIntro
import NewIntro from "./pages/NewIntro";

// Learn Your Notes — V1.3: menu + per-part route
import LearnYourNotesMenu from "./pages/LearnYourNotesMenu";
import LearnYourNotes from "./pages/LearnYourNotes";

// Levels (V1.1)
import Level1 from "./pages/Level-1";
import Level2 from "./pages/Level-2";
import Level3 from "./pages/Level-3";
import Level4 from "./pages/Level-4";
import Level5 from "./pages/Level-5";
import Level6 from "./pages/Level-6";
import Level7 from "./pages/Level-7";
import Level8 from "./pages/Level-8";
import Level9 from "./pages/Level-9";
import Level10 from "./pages/Level-10";

// Old mock paywall
import MockPaywall from "./pages/MockPaywall";

// V1.1 paywall + success
import PwpPaywall from "./pages/PwpPaywall";
import PwpPaymentSuccess from "./pages/PwpPaymentSuccess";
import MyPass from "./pages/MyPass";

// ✅ Home (V1.2) — NewHomeTwo is now the main landing page
import NewHomeTwo from "./pages/NewHomeTwo";
import NewHome from "./pages/NewHome"; // Old version kept for reference
import HomePageOne from "./pages/HomePageOne";
import NewHomeThree from "./pages/NewHomeThree";

// Trophy Cabinet
import TrophyCabinet from "./pages/TrophyCabinet";

// ✅ Treble Town Progress Map
import TrebleTownMap from "./pages/TrebleTownMap";

// ✅ Choose Your Path screen
import StartPath from "./pages/StartPath";

// ✅ Game Menu hub
import GameMenu from "./pages/GameMenu";

// Entitlement guard
import ProtectedLevelRoute from "./components/ProtectedLevelRoute";

// ✅ Cookie consent banner + footer link helper
import CookieConsentBanner from "./analytics/CookieConsentBanner";
import {
  openCookieSettings,
  maybeAutoLoadAnalytics,
} from "./analytics/cookieConsent";

// ✅ Route analytics tracker
import AnalyticsRouteTracker from "./analytics/AnalyticsRouteTracker";

// ✅ GA4 Event Tracking - NEW!
import { startSessionTracking, stopSessionTracking } from "./analytics/analytics";

// ✅ Practice — hub + free-play
import PracticeHub from "./pages/PracticeHub";
import PracticeMode from "./pages/PracticeMode";

// ✅ GAME MODE (V1.3) — 3D ball bouncer, separate from lesson levels
import GameLevel1 from "./pages/GameLevel1";
import CrystalBallRunner from "./pages/CrystalBallRunner";
import GameTargetPractice from "./pages/GameTargetPractice";
import LynGame from "./pages/LynGame";
// ✅ Learn-Your-Notes GAME — Note Trail map (separate from the song road map)
import LynMap from "./pages/LynMap";

// ✅ V4: Parent Progress Report
import ProgressReport from "./pages/ProgressReport";

/**
 * DEV-ONLY LOCAL HELPERS (SAFE)
 */
async function maybeInstallLocalUnlockHelpers() {
  const enabled =
    String(process.env.REACT_APP_ENABLE_LOCAL_UNLOCK || "").toLowerCase() ===
    "true";

  if (process.env.NODE_ENV !== "development" || !enabled) return;

  try {
    const mod = await import("./dev/localUnlock");
    if (mod && typeof mod.installLocalUnlockHelpers === "function") {
      mod.installLocalUnlockHelpers();
      // eslint-disable-next-line no-console
      console.log("🧪 Dev unlock helpers enabled (localhost only).");
    }
  } catch {
    // eslint-disable-next-line no-console
    console.warn("Dev unlock helpers not installed (missing ./dev/localUnlock).");
  }
}

/**
 * ✅ OAuth HASH CATCHER
 */
function OAuthHashCatcher() {
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const hash = window.location.hash || "";
    const hasTokens =
      hash.includes("access_token=") || hash.includes("refresh_token=");

    if (!hasTokens) return;

    if (location.pathname !== "/auth/callback") {
      navigate(`/auth/callback${location.search || ""}${hash}`, {
        replace: true,
      });
    }
  }, [navigate, location.pathname, location.search]);

  return null;
}

/**
 * TEMP "SOFT LOCK" NAV GUARD
 */
function SoftLockGuard({ children }) {
  const { pathname } = useLocation();
  const isDirectEntryRef = useRef(true);

  useEffect(() => {
    try {
      const navEntries = performance.getEntriesByType?.("navigation") || [];
      const navType = navEntries[0]?.type;
      isDirectEntryRef.current = Boolean(navType);
    } catch {
      isDirectEntryRef.current = true;
    }
  }, []);

  const allowList = new Set([
    "/",
    "/home",
    "/home-two",
    "/home-v11",
    "/landingpage",
    "/old-home",
    "/home-one",
    "/home-three",
    "/about",
    "/contact",
    "/pricing",
    "/parents",
    "/store",
    "/privacy-policy",
    "/terms",
    "/refund-policy",
    "/pwp-paywall",
    "/pwp-payment-success",
    "/my-pass",
    "/login",
    "/auth/callback",
    "/level-1",
    "/level-2",
    "/level-3",
    "/level-4",
    "/level-5",
    "/level-6",
    "/level-7",
    "/level-8",
    "/level-9",
    "/level-10",
    "/learn-your-notes",
    "/learn-your-notes/a",
    "/learn-your-notes/b",
    "/learn-your-notes/c",
    "/practice",
    "/practice/free",
    "/new-intro",
    "/trophy-cabinet",
    "/treble-town-map",
    "/start",
    "/game-menu",
    "/help",
    "/song-book",
    "/chapter-1",
    "/chapter-2",
    "/chapter-3",
    // ✅ V1.3: Game Mode routes
    "/game-level-1",
    "/crystal-ball-runner",
    "/target-practice",
    // ✅ Learn-Your-Notes game (Note Trail map + levels)
    "/lyn-map",
    "/lyn-game",
    // ✅ V4: Parent progress report
    "/progress",
  ]);

  // Allow all Learn-Your-Notes game routes (map + any /lyn-game/:levelId).
  const isLynRoute =
    pathname.startsWith("/lyn-game") || pathname.startsWith("/lyn-map");

  if (!isDirectEntryRef.current && !allowList.has(pathname) && !isLynRoute) {
    return <Navigate to="/home" replace />;
  }

  return children;
}


function App() {
  useEffect(() => {
    maybeInstallLocalUnlockHelpers();

    // ✅ If user previously accepted analytics, load GA silently on app start
    maybeAutoLoadAnalytics();

    // ✅ NEW: Start session time tracking (fires after user accepts cookies)
    startSessionTracking();

    const loadAndPredict = async () => {
      try {
        const model = await tf.loadLayersModel(
          "/models/melody_model_js/model.json"
        );
        const input = tf.tensor2d([[1, 0, 0, 0, 0, 0, 0]]);
        const prediction = model.predict(input);
        prediction.array().then((data) =>
          console.log("🎵 Prediction output:", data)
        );
      } catch (err) {
        console.error("❌ Failed to load or use melody model:", err);
      }
    };

    loadAndPredict();

    return () => {
      stopSessionTracking();
    };
  }, []);

  return (
    <AuthProvider>
      <Router>
        <PracticeTimerProvider>
        <SoftLockGuard>
          <OAuthHashCatcher />
          <AnalyticsRouteTracker />

          <div className="min-h-screen bg-transparent flex flex-col">
            <main className="flex-1">
              <Routes>
                {/* ✅ Root and all home variants */}
                <Route path="/" element={<Navigate to="/home" replace />} />
                <Route path="/home" element={<NewHomeTwo />} />
                <Route path="/home-two" element={<NewHomeTwo />} />
                <Route path="/home-v11" element={<NewHomeTwo />} />
                <Route path="/landingpage" element={<NewHomeTwo />} />

                <Route path="/old-home" element={<LandingPage />} />
                <Route path="/old-home-v1" element={<NewHome />} />
                <Route path="/home-one" element={<HomePageOne />} />
                <Route path="/home-three" element={<NewHomeThree />} />

                {/* ✅ Auth routes */}
                <Route path="/login" element={<Login />} />
                <Route path="/auth/callback" element={<AuthCallback />} />

                <Route path="/intro" element={<Intro />} />
                <Route path="/new-intro" element={<NewIntro />} />

                {/* Learn Your Notes */}
                <Route path="/learn-your-notes" element={<LearnYourNotesMenu />} />
                <Route path="/learn-your-notes/:partId" element={<LearnYourNotes />} />

                {/* ✅ Practice */}
                <Route path="/practice" element={<PracticeHub />} />
                <Route path="/practice/free" element={<PracticeMode />} />
                <Route path="/practice-mode" element={<Navigate to="/practice" replace />} />

                {/* ✅ Help + Song Book */}
                <Route path="/help" element={<Help />} />
                <Route path="/song-book" element={<SongBook />} />

                {/* ✅ V4: Parent Progress Report */}
                <Route path="/progress" element={<ProgressReport />} />

                {/* Paywall */}
                <Route path="/pwp-paywall" element={<PwpPaywall />} />
                <Route path="/pwp-payment-success" element={<PwpPaymentSuccess />} />
                <Route path="/my-pass" element={<MyPass />} />

                {/* ✅ Levels */}
                <Route path="/level-1" element={<ProtectedLevelRoute levelId={1}><Level1 /></ProtectedLevelRoute>} />
                <Route path="/level-2" element={<ProtectedLevelRoute levelId={2}><Level2 /></ProtectedLevelRoute>} />
                <Route path="/level-3" element={<ProtectedLevelRoute levelId={3}><Level3 /></ProtectedLevelRoute>} />
                <Route path="/level-4" element={<ProtectedLevelRoute levelId={4}><Level4 /></ProtectedLevelRoute>} />
                <Route path="/level-5" element={<ProtectedLevelRoute levelId={5}><Level5 /></ProtectedLevelRoute>} />
                <Route path="/level-6" element={<ProtectedLevelRoute levelId={6}><Level6 /></ProtectedLevelRoute>} />
                <Route path="/level-7" element={<ProtectedLevelRoute levelId={7}><Level7 /></ProtectedLevelRoute>} />
                <Route path="/level-8" element={<ProtectedLevelRoute levelId={8}><Level8 /></ProtectedLevelRoute>} />
                <Route path="/level-9" element={<ProtectedLevelRoute levelId={9}><Level9 /></ProtectedLevelRoute>} />
                <Route path="/level-10" element={<ProtectedLevelRoute levelId={10}><Level10 /></ProtectedLevelRoute>} />

                {/* Trophy Cabinet */}
                <Route path="/trophy-cabinet" element={<TrophyCabinet />} />

                {/* ✅ Treble Town Progress Map */}
                <Route path="/treble-town-map" element={<TrebleTownMap />} />

                {/* ✅ Choose Your Path */}
                <Route path="/start" element={<StartPath />} />

                {/* ✅ Game Menu */}
                <Route path="/game-menu" element={<GameMenu />} />

                {/* ✅ GAME MODE */}
                <Route path="/game-level-1" element={<GameLevel1 />} />
                <Route path="/crystal-ball-runner" element={<CrystalBallRunner />} />
                <Route path="/target-practice" element={<GameTargetPractice />} />

                {/* ✅ LEARN-YOUR-NOTES GAME */}
                <Route path="/lyn-map" element={<LynMap />} />
                <Route path="/lyn-game/:levelId" element={<LynGame />} />
                <Route path="/lyn-game" element={<LynGame />} />

                {/* ✅ Store */}
                <Route path="/store" element={<Store />} />

                <Route path="/paywall" element={<MockPaywall />} />
                <Route path="/chapter-4" element={<Navigate to="/level-4" replace />} />
                <Route path="/chapter-1" element={<ChapterOne />} />
                <Route path="/chapter-2" element={<ChapterTwo />} />
                <Route path="/chapter-3" element={<Navigate to="/level-3" replace />} />

                <Route path="/chapter-4-legacy" element={<RequireCh1to3Complete><RequirePremium><ChapterFourPage /></RequirePremium></RequireCh1to3Complete>} />
                <Route path="/chapter-5" element={<RequireCh1to3Complete><RequirePremium><ChapterFivePage /></RequirePremium></RequireCh1to3Complete>} />
                <Route path="/chapter-6" element={<RequireCh1to3Complete><RequirePremium><ChapterSixPage /></RequirePremium></RequireCh1to3Complete>} />
                <Route path="/chapter-7" element={<RequireCh1to3Complete><RequirePremium><ChapterSevenPage /></RequirePremium></RequireCh1to3Complete>} />
                <Route path="/chapter-8" element={<RequireCh1to3Complete><RequirePremium><ChapterEightPage /></RequirePremium></RequireCh1to3Complete>} />
                <Route path="/chapter-9" element={<RequireCh1to3Complete><RequirePremium><ChapterNinePage /></RequirePremium></RequireCh1to3Complete>} />
                <Route path="/chapter-10" element={<RequireCh1to3Complete><RequirePremium><ChapterTenPage /></RequirePremium></RequireCh1to3Complete>} />

                <Route path="/constellation" element={<ConstellationPage />} />
                <Route path="/victory" element={<DoronDefeated />} />
                <Route path="/certificate" element={<CompletionCertificate />} />
                <Route path="/subscribe" element={<SubscribePage />} />
                <Route path="/pay/success" element={<PaySuccess />} />

                {/* Policy/info */}
                <Route path="/about" element={<About />} />
                <Route path="/parents" element={<Parents />} />
                <Route path="/contact" element={<Contact />} />
                <Route path="/pricing" element={<Navigate to="/pwp-paywall" replace />} />
                <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                <Route path="/terms" element={<Terms />} />
                <Route path="/refund-policy" element={<RefundPolicy />} />

                <Route path="*" element={<Navigate to="/home" replace />} />
              </Routes>
            </main>

            <footer className="border-t bg-white print:hidden">
              <div className="max-w-5xl mx-auto px-4 py-4 text-sm flex flex-wrap gap-x-4 gap-y-2 items-center justify-between">
                <nav className="flex flex-wrap gap-4">
                  <Link to="/about" className="hover:underline">About</Link>
                  <Link to="/parents" className="hover:underline">Parents</Link>
                  <Link to="/store" className="hover:underline">Store</Link>
                  <Link to="/contact" className="hover:underline">Contact</Link>
                  <Link to="/practice" className="hover:underline">Practice</Link>
                  <Link to="/privacy-policy" className="hover:underline">Privacy Policy</Link>
                  <Link to="/terms" className="hover:underline">Terms &amp; Conditions</Link>
                  <Link to="/refund-policy" className="hover:underline">Cancellation/Refund Policy</Link>
                  <button
                    type="button"
                    onClick={() => openCookieSettings()}
                    className="hover:underline text-slate-700"
                  >
                    Cookie Settings
                  </button>
                </nav>
                <div className="text-slate-600">
                  © 2026 Piano With Pan™ • Piano For Everyone By Nishat Chadha.
                  All rights reserved.
                </div>
              </div>
            </footer>

            <CookieConsentBanner />
          </div>
        </SoftLockGuard>
        </PracticeTimerProvider>
      </Router>
    </AuthProvider>
  );
}

export default App;